import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { EASE, Eyebrow } from "@/components/site/motion";
import { scrollToHash } from "@/lib/scroll";

function Segments({ segments }) {
  return segments.map((s, i) =>
    s.link ? (
      <Link key={i} to={s.to} className="font-medium text-blue-600 underline decoration-blue-600/30 underline-offset-4 transition-colors hover:decoration-blue-600">
        {s.link}
      </Link>
    ) : s.strong ? (
      <strong key={i} className="font-semibold text-slate-900">{s.t}</strong>
    ) : (
      <span key={i}>{s.t}</span>
    )
  );
}

function Block({ block }) {
  if (block.type === "p") {
    return (
      <p className="mt-4 text-[15.5px] leading-[1.85] text-slate-600">
        {block.segments ? <Segments segments={block.segments} /> : block.text}
      </p>
    );
  }
  if (block.type === "h3") {
    return <h3 className="mt-9 text-[17px] font-semibold tracking-tight text-slate-900">{block.text}</h3>;
  }
  if (block.type === "list") {
    return (
      <ul className="mt-4 space-y-2.5">
        {block.items.map((item) => (
          <li key={item} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-600">
            <span className="mt-[9px] h-px w-4 shrink-0 bg-gradient-to-r from-blue-600 to-violet-600" aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "table") {
    return (
      <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {block.headers.map((h) => (
                <th key={h} className="px-5 py-3.5 font-mono text-[10.5px] font-medium uppercase tracking-[0.18em] text-slate-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr key={ri} className="border-b border-slate-100 last:border-0">
                {row.map((cell, ci) => (
                  <td key={ci} className={`px-5 py-4 align-top ${ci === 0 ? "font-medium text-slate-900" : "text-slate-600"}`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (block.type === "contact") {
    return (
      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-7">
        <p className="text-[15px] font-semibold text-slate-900">{block.company}</p>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          {block.address.map((line) => (
            <span key={line} className="block">{line}</span>
          ))}
        </p>
        <div className="mt-4 space-y-1.5 text-sm text-slate-600">
          <p>
            <span className="font-medium text-slate-900">Email:</span>{" "}
            <a href={`mailto:${block.email}`} className="text-blue-600 transition-colors hover:text-blue-700">{block.email}</a>
          </p>
          <p>
            <span className="font-medium text-slate-900">Phone:</span>{" "}
            <a href="tel:+919305383099" className="text-blue-600 transition-colors hover:text-blue-700">{block.phone}</a>
          </p>
          <p>
            <span className="font-medium text-slate-900">CIN:</span>{" "}
            <span className="font-mono text-[13px] tracking-wide">{block.cin}</span>
          </p>
          <p>
            <span className="font-medium text-slate-900">GSTIN:</span>{" "}
            <span className="font-mono text-[13px] tracking-wide">{block.gstin}</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
}

export default function LegalPage({ content }) {
  return (
    <main className="bg-white pt-[72px]" data-testid="legal-page">
      <div className="border-b border-slate-200">
        <div className="mx-auto w-full max-w-[1200px] px-6 py-16 md:px-8 md:py-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
            <Eyebrow>Legal</Eyebrow>
            <h1 data-testid="legal-page-title" className="mt-5 text-4xl font-extrabold tracking-[-0.02em] text-slate-900 md:text-[3.4rem] md:leading-[1.05]">
              {content.title}
            </h1>
            <p className="mt-5 max-w-[720px] text-[15px] leading-relaxed text-slate-600 md:text-base">
              {content.heroCopy}
            </p>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.24em] text-slate-400" data-testid="legal-last-updated">
              Last Updated: {content.updated}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-[1200px] gap-16 px-6 py-16 md:px-8 md:py-20 lg:grid-cols-[230px_1fr]">
        <aside className="hidden lg:block">
          <div className="sticky top-28">
            <p className="font-mono text-[10.5px] uppercase tracking-[0.24em] text-slate-400">On this page</p>
            <nav className="mt-5 flex flex-col gap-1" aria-label="Table of contents" data-testid="legal-toc">
              {(content.toc || content.sections.map((s) => ({ label: s.title, id: s.id }))).map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`toc-${item.id}`}
                  onClick={() => scrollToHash(`#legal-${item.id}`)}
                  className="group flex items-baseline gap-2.5 rounded-lg px-2 py-1.5 text-left text-[13px] text-slate-500 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-900"
                >
                  <span className="font-mono text-[10px] text-slate-300 transition-colors group-hover:text-blue-600">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>

        <article className="max-w-[780px]">
          {content.intro.map((p, i) => (
            <p key={i} className="mb-4 text-[15.5px] leading-[1.85] text-slate-600">
              {p}
            </p>
          ))}
          {content.sections.map((s, i) => (
            <motion.section
              key={s.id}
              id={`legal-${s.id}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE }}
              className="mt-14 scroll-mt-28 border-t border-slate-200 pt-10 first:mt-10"
            >
              <h2 className="flex items-baseline gap-4 text-[26px] font-bold tracking-[-0.01em] text-slate-900">
                <span className="font-mono text-xs font-normal tracking-[0.16em] text-slate-300">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {s.title}
              </h2>
              {s.blocks.map((b, bi) => (
                <Block key={bi} block={b} />
              ))}
            </motion.section>
          ))}
        </article>
      </div>
    </main>
  );
}
